/**
 * modules/CVScreener.jsx
 * AI-powered CV screening — bulk upload, parse, score, voice interview prep
 */

import { useState, useEffect } from "react";
import { api, Spinner } from "../utils/api";
import { HeroHeader, StatRow, Section, GradientAvatar, ScoreBar, EmptyState, TierBadge, IconChip, RowItem } from "../components/UI";

export function CVScreener() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    api.get('/api/screening/sessions').catch(() => []).then(d => {
      setSessions(d || []);
      setLoading(false);
    });
  }, []);

  if (loading) return <Spinner/>;

  const tabs = [
    { id:'all',       label:'All sessions' },
    { id:'pending',   label:'Pending review' },
    { id:'scored',    label:'Scored' },
    { id:'completed', label:'Completed' },
  ];

  const filtered = activeTab === 'all' ? sessions : sessions.filter(s => s.status === activeTab);

  return (
    <>
      <HeroHeader
        title="CV Screener"
        subtitle="BULK CV UPLOAD · CLAUDE-POWERED PARSING · AI MATCH SCORING"
        badge="AI"
      />

      <StatRow items={[
        { label:'Total sessions',  val: sessions.length, cls:'or', icon:'▣' },
        { label:'CVs processed',   val: sessions.reduce((a,s) => a + (s.cv_count || 0), 0), cls:'gd', icon:'◫' },
        { label:'High-match',      val: sessions.filter(s => (s.avg_score || 0) >= 70).length, cls:'tl', icon:'★' },
        { label:'Avg match score', val: sessions.length ? `${Math.round(sessions.reduce((a,s) => a + (s.avg_score || 0), 0) / sessions.length)}` : '—', cls:'bl', icon:'✦' },
      ]}/>

      {/* Upload zone */}
      <div className="card animate-in" style={{
        background: 'linear-gradient(135deg, rgba(255,112,9,0.04) 0%, rgba(255,6,99,0.04) 100%)',
        border: '1px dashed rgba(255,92,26,0.3)',
        padding: '32px 24px',
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.15s',
        flexShrink: 0,
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(255,92,26,0.5)'; e.currentTarget.style.background='linear-gradient(135deg, rgba(255,112,9,0.08) 0%, rgba(255,6,99,0.06) 100%)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(255,92,26,0.3)'; e.currentTarget.style.background='linear-gradient(135deg, rgba(255,112,9,0.04) 0%, rgba(255,6,99,0.04) 100%)'; }}>
        <div style={{
          width:56, height:56, borderRadius:14,
          background:'linear-gradient(135deg, var(--or-start), var(--or-end))',
          display:'inline-flex', alignItems:'center', justifyContent:'center',
          fontSize:24, marginBottom:14, color:'#fff',
        }}>▲</div>
        <div style={{fontSize:14, fontWeight:700, color:'#fff', marginBottom:6, letterSpacing:'-0.01em'}}>Upload CVs to start screening</div>
        <div style={{fontSize:12, color:'#999', fontFamily:'var(--mono)'}}>PDF · DOCX · TXT — up to 50 files at once · max 20MB each</div>
        <button className="btn primary" style={{marginTop:18, padding:'10px 24px'}}>SELECT FILES</button>
      </div>

      {/* Tabs */}
      <div style={{display:'flex', gap:4, background:'var(--card2)', borderRadius:10, padding:4, alignSelf:'flex-start'}}>
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            style={{
              padding:'7px 16px', borderRadius:7, border:'none', cursor:'pointer',
              fontFamily:'var(--sans)', fontSize:12, fontWeight:600,
              background: activeTab === t.id ? 'var(--or)' : 'transparent',
              color: activeTab === t.id ? '#fff' : '#999',
              transition: 'all 0.15s',
            }}
          >{t.label}</button>
        ))}
      </div>

      {/* Sessions list */}
      <Section title="Screening sessions" action="NEW SESSION +">
        {filtered.length === 0 ? (
          <EmptyState
            icon="▣"
            title={activeTab === 'all' ? 'No sessions yet' : `No ${activeTab} sessions`}
            message="Upload CVs above to start your first AI screening session."
          />
        ) : filtered.map(s => (
          <RowItem
            key={s.id}
            left={<IconChip icon="▣"/>}
            title={s.brief_title || `Screening session ${s.id?.slice(0,8) || ''}`}
            subtitle={`${s.cv_count || 0} CVs · ${s.scored_count || 0} scored · created ${s.created_ago || 'recently'}`}
            right={s.avg_score ? <ScoreBar score={s.avg_score}/> : <TierBadge tier={s.status || 'Pending'}/>}
          />
        ))}
      </Section>
    </>
  );
}
